import {
	computed,
	inject,
	Injectable,
	Signal,
	signal,
	WritableSignal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@tolgee/ngx';

import { IAuCalendarLayoutDay } from '@shared/ui-components/au-calendar-layout/interfaces/au-calendar-layout-day.interface';
import { AuCalendarDatePeriods } from '@shared/ui-components/au-calendar-layout/interfaces/date-periods.type';

const MONTH_KEYS = [
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
] as const;

@Injectable()
export class AuCalendarLayoutService {
	private readonly datePipe = inject(DatePipe);
	private readonly translateService = inject(TranslateService);

	activePeriod: WritableSignal<AuCalendarDatePeriods> = signal('month');

	weekdays: Signal<string[]> = signal([
		'monday',
		'tuesday',
		'wednesday',
		'thursday',
		'friday',
		'saturday',
		'sunday',
	]);
	days: WritableSignal<IAuCalendarLayoutDay[]> = signal([]);

	today: Date = new Date();
	month: WritableSignal<number> = signal(this.today.getMonth());
	year: WritableSignal<number> = signal(this.today.getFullYear());

	currentWeekStart: WritableSignal<Date> = signal(this.getWeekStartDate(this.today));
	currentWeekEnd: WritableSignal<Date> = signal(this.getWeekEndDate(this.today));

	monthName: Signal<string> = computed(() => {
		if (this.activePeriod() === 'month') {
			const date = new Date(this.year(), this.month());
			return `${this.translateService.instant(MONTH_KEYS[date.getMonth()])} ${this.year()}`;
		}

		const weekStartDate = this.currentWeekStart();
		const weekEndDate = this.currentWeekEnd();

		const startMonth = this.translateService.instant(
			MONTH_KEYS[weekStartDate.getMonth()]
		);
		const startYear = weekStartDate.getFullYear();

		const endMonth = this.translateService.instant(MONTH_KEYS[weekEndDate.getMonth()]);
		const endYear = weekEndDate.getFullYear();

		// week can span across two months or two years
		if (startYear === endYear) {
			// if year is the same but month is different, then we have a week that spans across two months
			if (weekStartDate.getMonth() !== weekEndDate.getMonth()) {
				// Aug - Sep 2023
				return `${startMonth} – ${endMonth} ${startYear}`;
			} else {
				// August 2023
				return `${startMonth} ${startYear}`;
			}
		} else {
			// Dec 2023 – Jan 2024
			return `${startMonth} ${startYear} – ${endMonth} ${endYear}`;
		}
	});

	weekNumber: Signal<number> = computed(() => {
		const weekStartDate = this.currentWeekStart();
		// + at the beginning converts the string to a number
		return +this.datePipe.transform(weekStartDate, 'w');
	});

	calendarDateRange: Signal<{ from: string; to: string }> = computed(() => {
		if (this.days().length) {
			const firstCellDate = this.days()[0].dateObject;
			const lastCellDate = this.days()[this.days().length - 1].dateObject;

			return {
				//prettier-ignore
				from: `${this.datePipe.transform(firstCellDate, 'yyyy-MM-dd')}T00:00:00`,
				to: `${this.datePipe.transform(lastCellDate, 'yyyy-MM-dd')}T23:59:59`,
			};
		}
	});

	maxItemsPerDay: Signal<number> = computed(() => {
		const activePeriod = this.activePeriod();
		return activePeriod === 'week' ? 100 : 2;
	});

	previousPeriodRange() {
		if (this.activePeriod() === 'month') {
			this.previousMonth();
		} else if (this.activePeriod() === 'week') {
			this.previousWeek();
		}
	}

	nextPeriodRange() {
		if (this.activePeriod() === 'month') {
			this.nextMonth();
		} else if (this.activePeriod() === 'week') {
			this.nextWeek();
		}
	}

	previousMonth(): void {
		const prevMonthNumber = this.month() - 1;
		if (prevMonthNumber < 0) {
			this.month.set(11);
			this.year.update(year => year - 1);
		} else {
			this.month.set(prevMonthNumber);
		}
		this.generateMonthCalendar();

		this.setWeekStartAndEndBasedOnMonth();
	}

	nextMonth(): void {
		const nextMonthNumber = this.month() + 1;
		if (nextMonthNumber > 11) {
			this.month.set(0);
			this.year.update(year => year + 1);
		} else {
			this.month.set(nextMonthNumber);
		}
		this.generateMonthCalendar();

		this.setWeekStartAndEndBasedOnMonth();
	}

	goToToday(): void {
		this.setMonthAndYear(this.today.getMonth(), this.today.getFullYear());
		this.setWeekStartAndEnd(
			this.getWeekStartDate(this.today),
			this.getWeekEndDate(this.today)
		);

		if (this.activePeriod() === 'month') {
			this.generateMonthCalendar();
		} else if (this.activePeriod() === 'week') {
			this.generateWeekCalendar();
		}
	}

	generateMonthCalendar(): void {
		const days = [];

		const firstDay = new Date(this.year(), this.month(), 1);
		const lastDay = new Date(this.year(), this.month() + 1, 0);
		const daysAmount = lastDay.getDate();

		const firstWeekday = firstDay.getDay() === 0 ? 7 : firstDay.getDay();

		// Add empty cells for previous month
		const prevMonthLastDay = new Date(this.year(), this.month(), 0).getDate();
		const prevMonthDays = firstWeekday - 1;

		for (
			let date = prevMonthLastDay - prevMonthDays + 1;
			date <= prevMonthLastDay;
			date++
		) {
			const dateObject = new Date(this.year(), this.month() - 1, date);
			const day: IAuCalendarLayoutDay = {
				date,
				isCurrentMonth: false,
				dateObject: this.datePipe.transform(dateObject, 'yyyy-MM-dd'),
			};
			days.push(day);
		}

		// Add cells for current month
		for (let date = 1; date <= daysAmount; date++) {
			const dateObject = new Date(this.year(), this.month(), date);
			const day: IAuCalendarLayoutDay = {
				date,
				isCurrentMonth: true,
				dateObject: this.datePipe.transform(dateObject, 'yyyy-MM-dd'),
			};
			days.push(day);
		}

		// Add empty cells for next month
		const numCells = days.length;
		const remainingCells = Math.ceil(numCells / 7) * 7 - numCells;
		for (let date = 1; date <= remainingCells; date++) {
			const dateObject = new Date(this.year(), this.month() + 1, date);
			const day: IAuCalendarLayoutDay = {
				date,
				isCurrentMonth: false,
				dateObject: this.datePipe.transform(dateObject, 'yyyy-MM-dd'),
			};
			days.push(day);
		}

		this.days.set(days);
	}

	setActivePeriod(period: AuCalendarDatePeriods): void {
		if (period === this.activePeriod()) {
			return;
		}

		this.activePeriod.set(period);

		if (period === 'month') {
			this.generateMonthCalendar();
		} else if (period === 'week') {
			this.generateWeekCalendar();
		}
	}

	private getWeekStartDate(date: Date): Date {
		const currentDate = date || this.today;
		const dayOfWeek = currentDate.getDay(); // 0 for Sunday, 1 for Monday, etc.
		const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
		const startDate = new Date(currentDate);
		startDate.setDate(startDate.getDate() - diff);
		return startDate;
	}

	private getWeekEndDate(date: Date): Date {
		const startDate = this.getWeekStartDate(date);
		const endDate = new Date(startDate);
		endDate.setDate(startDate.getDate() + 6); // 6 days later
		return endDate;
	}

	previousWeek(): void {
		this.setWeekAndGenerateCalendar(-1);
	}

	nextWeek(): void {
		this.setWeekAndGenerateCalendar(1);
	}

	generateWeekCalendar(): void {
		const days = [];
		const weekStartDate = this.currentWeekStart();
		const weekEndDate = this.currentWeekEnd();

		// Loop through the days of the week
		const currentDate = new Date(weekStartDate);

		while (currentDate <= weekEndDate) {
			const dateObject = new Date(currentDate);
			const day: IAuCalendarLayoutDay = {
				date: currentDate.getDate(),
				isCurrentMonth: true, // All days in the week view are considered part of the current month
				dateObject: this.datePipe.transform(dateObject, 'yyyy-MM-dd'),
			};

			days.push(day);

			// Move to the next day
			currentDate.setDate(currentDate.getDate() + 1);
		}

		this.days.set(days);
	}

	private setMonthAndYear(month: number, year: number): void {
		this.month.set(month);
		this.year.set(year);
	}

	private setWeekStartAndEnd(start: Date, end: Date): void {
		this.currentWeekStart.set(start);
		this.currentWeekEnd.set(end);
	}

	private setWeekAndGenerateCalendar(offset: number): void {
		const weekStartDate = new Date(this.currentWeekStart());
		weekStartDate.setDate(weekStartDate.getDate() + offset * 7);

		const weekEndDate = new Date(weekStartDate);
		weekEndDate.setDate(weekEndDate.getDate() + 6);

		this.setWeekStartAndEnd(weekStartDate, weekEndDate);
		this.setMonthAndYear(weekStartDate.getMonth(), weekStartDate.getFullYear());

		this.generateWeekCalendar();
	}

	private setWeekStartAndEndBasedOnMonth(): void {
		let currentDate;
		// if we are in the same month as today, then we need to set the week start and end based on today
		if (this.month() === this.today.getMonth()) {
			currentDate = this.today;
		} else {
			currentDate = new Date(this.year(), this.month(), 1);
		}

		this.setWeekStartAndEnd(
			this.getWeekStartDate(currentDate),
			this.getWeekEndDate(currentDate)
		);
	}
}
